import {
  type CheckText,
  type DraftView,
  type GoldenResult,
  PUBLISH_BASIS_KINDS,
  type PublishResponse,
  type RiskText,
  type RuleGroupView,
  type RulesView,
} from '@tabel/shared/schemas/rules';
import type { PlanResponse } from '@tabel/shared/schemas/rules-plan';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, ChartColumn, Info, Send, TriangleAlert } from 'lucide-react';
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { errorText, useToast } from '../../components/ErrorToast';
import { AlertDialog, Button, Callout, Modal, Skeleton, inputCls, textareaCls } from '../../components/ui';
import { api, rulesKeys } from '../../lib/api';
import { ApiError, isTransient, reportClientError } from '../../lib/errors';
import { ruDate, ruDateTime } from '../../lib/format';
import { useRole } from '../../lib/role';
import { loadPreview } from './ChangesView';
import { DaysDialog } from './DaysDialog';
import { impactLine } from './ImpactLines';
import { shouldReport } from './LoadError';
import { ChecksList } from './RuleEditor';
import { localDate } from './dates';
import { goldenChangeText } from './golden';
import { PLAN_T, sayPlan } from './plan-texts';
import { BASIS_KIND_TEXT, LABEL, T, say } from './texts';

/*
 * Окно «Опубликовать изменения» (004, US3, T082; contracts/ui.md, «Окна»; FR-033–FR-037, FR-039; прототип v6):
 * - «Что меняется» — изменения черновика словами;
 * - «Действует с» — по умолчанию `defaultValidFrom`, не раньше `earliestValidFrom`; дата внутри неподписанного
 *   табеля — предупреждение «две версии»; граница — будущая версия — как изменить правила раньше;
 * - влияние с этой даты (считает сервер, `POST /api/rules/draft/preview`), «В каких днях»;
 * - проверочные примеры «было → станет» с «Так и должно быть», предупреждения с «Понимаю риск» — запросами ведущего;
 * - «Почему меняем» (4 варианта), «Что решили», «Номер и дата документа» (только у приказа), «Вопрос реестра»,
 *   «Список дней, в которых изменятся часы, просмотрен», «Вы и автор, и тот, кто публикует…»;
 * - причина недоступности «Опубликовать» — рядом с кнопкой (FR-037);
 * - `PREVIEW_STALE` — влияние считается заново, окно ждёт повторного нажатия (FR-039).
 * Ошибка публикации — тост с кодом, введённое остаётся (FR-061). Esc и «Отмена» при введённом сначала спрашивают.
 * Часы окно не считает: числа влияния и примеров — с сервера.
 *
 * Публикация по плану ИИ-агента (005, US5, T122; FR-038; `plan`): метка «Подготовлено ИИ-агентом» с клиентом и
 * временем заморозки; «Действует с» — дата плана без выбора (другая дата — новый план); основание — из плана, человек
 * может его изменить; запрос — тот же, с `planId`. Отметки «Так и должно быть» и «Понимаю риск» — как в 004, из
 * черновика и влияния. `409 PLAN_STALE` — окно закрывается (`onStale`), страница перечитывает план: панель покажет
 * причину. `409 PREVIEW_STALE` — влияние считается заново, план перечитывается: панель покажет числа «Сейчас».
 */

const ISO = /^\d{4}-\d{2}-\d{2}$/u;
/** Сколько ждать после смены даты, прежде чем считать влияние заново (предел — 6 расчётов в минуту). */
const DATE_DELAY_MS = 600;

type BasisKind = (typeof PUBLISH_BASIS_KINDS)[number];

/** Табель, который дата начала делит на две версии правил (FR-035). В схеме ответа влияния поля пока нет. */
interface SplitTimesheet {
  readonly id: number;
  readonly name: string;
}

function splitOf(preview: unknown): SplitTimesheet[] {
  const list = (preview as { splitTimesheets?: unknown } | undefined)?.splitTimesheets;
  return Array.isArray(list)
    ? list.filter((t): t is SplitTimesheet => typeof t?.name === 'string' && typeof t?.id === 'number')
    : [];
}

/** Пример уже подтверждён «Так и должно быть» (сервер может прислать `confirmed`, в схеме поля пока нет). */
const confirmedOnServer = (g: GoldenResult) => (g as { confirmed?: unknown }).confirmed === true;

const byCode = <X extends { code: string }>(list: readonly X[]) =>
  list.filter((x, i, all) => all.findIndex((y) => y.code === x.code) === i);
const checkKey = (e: CheckText) => `${e.code}|${e.cube ?? ''}|${e.field ?? ''}`;

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section aria-label={label} className="space-y-1.5">
      <h3 className="text-[13.5px] font-medium text-ink">{label}</h3>
      {children}
    </section>
  );
}

const CHECK = 'relative top-[2px] h-4 w-4 shrink-0 accent-accent';
const CHOICE = 'flex cursor-pointer items-start gap-2.5 text-[13.5px] max-sm:min-h-10';

function PublishBody({
  view,
  draft,
  plan,
  tzOffset,
  onCancel,
  onPublished,
  onDirty,
  onStale,
}: {
  view: RulesView;
  draft: DraftView;
  plan?: PlanResponse | undefined;
  tzOffset: string;
  onCancel: () => void;
  onPublished: (res: PublishResponse) => void;
  onDirty: (dirty: boolean) => void;
  onStale: () => void;
}) {
  const ids = useId();
  const { role } = useRole();
  const qc = useQueryClient();
  const { showError, showInfo } = useToast();

  // `etag` черновика: «Так и должно быть» и «Понимаю риск» его меняют, публикация идёт с последним.
  const etag = useRef(draft.etag);
  useEffect(() => {
    etag.current = draft.etag;
  }, [draft.etag]);

  // «Действует с»: введённое и дата, по которой посчитано влияние (меняется через 600 мс после ввода). По плану — дата
  // плана, без выбора: публикация по плану — только с ней (FR-038).
  const startDate = plan?.validFrom ?? draft.defaultValidFrom;
  const [dateInput, setDateInput] = useState(startDate);
  const [seed, setSeed] = useState({ etag: draft.etag, validFrom: startDate, n: 0 });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** «Данные изменились, влияние посчитано заново» — после `PREVIEW_STALE`, до следующего нажатия. */
  const [stale, setStale] = useState(false);
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);
  const dateOk = ISO.test(dateInput) && dateInput >= draft.earliestValidFrom;
  const onDate = (v: string) => {
    setDateInput(v);
    if (timer.current) clearTimeout(timer.current);
    if (!ISO.test(v) || v < draft.earliestValidFrom) return;
    timer.current = setTimeout(() => {
      setStale(false);
      setSeed((s) => (s.validFrom === v ? s : { etag: etag.current, validFrom: v, n: s.n }));
    }, DATE_DELAY_MS);
  };
  const validFrom = seed.validFrom;

  const preview = useQuery({
    queryKey: rulesKeys.publishPreview(role, seed.etag, validFrom, seed.n),
    queryFn: async () => {
      try {
        return await loadPreview(seed.etag, validFrom);
      } catch (e) {
        if (shouldReport(e)) reportClientError('Rules', 'publish preview', e);
        throw e;
      }
    },
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const p = preview.data;
  // Новая дата ждёт расчёта; неверная дата не считается — остаётся влияние прежней даты с её подписью.
  const pending = preview.isFetching || (dateOk && dateInput !== validFrom);
  const failed = preview.isError || p?.status === 'failed';

  // Граница — будущая версия (FR-035, сценарий 3): когда она опубликована — из истории версий.
  const futureBoundary = draft.baseLabel === 'future' && draft.baseValidFrom === draft.earliestValidFrom;
  const history = useQuery({
    queryKey: rulesKeys.history(role),
    queryFn: () => api.rulesHistory(),
    enabled: futureBoundary,
  });
  const base = history.data?.find((v) => v.id === draft.baseVersionId);
  const registry = useQuery({ queryKey: rulesKeys.registry(role), queryFn: () => api.rulesRegistry() });

  const byId = useMemo(
    () => new Map<string, RuleGroupView>(view.groups.map((g) => [g.id, g])),
    [view.groups],
  );

  // Проверочные примеры, у которых по черновику меняется результат (FR-036).
  const goldens = (p?.goldens ?? []).filter((g) => !g.passed);
  const [confirmed, setConfirmed] = useState<Readonly<Record<number, boolean>>>({});
  const [goldenBusy, setGoldenBusy] = useState<number | null>(null);
  const isConfirmed = (g: GoldenResult) => confirmed[g.goldenId] ?? confirmedOnServer(g);

  // Предупреждения черновика и влияния (FR-025, FR-033).
  const risks = byCode([...draft.risks, ...(p?.risks ?? [])]);
  const [acked, setAcked] = useState<Readonly<Record<string, boolean>>>({});
  const [riskBusy, setRiskBusy] = useState<string | null>(null);
  const isAcked = (r: RiskText) => acked[r.code] === true || r.acked;

  const errors = [...draft.errors, ...(p?.errors ?? [])].filter(
    (e, i, all) => all.findIndex((x) => checkKey(x) === checkKey(e)) === i,
  );
  const calcErrors = (p?.days ?? []).filter((d) => d.error).length;

  // Основание (FR-034). По плану — предложение агента: вид, «Что решили», вопрос реестра; человек может изменить.
  const [proposed] = useState<{ kind: BasisKind | ''; decided: string; question: string }>(() => ({
    kind: plan?.basis.kind ?? '',
    decided: plan?.basis.decided ?? '',
    question: plan?.basis.questionCode ?? '',
  }));
  const [kind, setKind] = useState<BasisKind | ''>(proposed.kind);
  const [decided, setDecided] = useState(proposed.decided);
  const [doc, setDoc] = useState('');
  const [question, setQuestion] = useState(proposed.question);
  const [seen, setSeen] = useState(false);
  const isDoc = kind === 'приказ_распоряжение';
  const [days, setDays] = useState(false);

  const dirty =
    kind !== proposed.kind ||
    decided.trim() !== proposed.decided.trim() ||
    doc.trim() !== '' ||
    question !== proposed.question ||
    seen ||
    dateInput !== startDate;
  useEffect(() => onDirty(dirty), [dirty, onDirty]);

  /** Страница перечитывает правила и черновик; влияние окна остаётся своим. */
  const refreshRules = () =>
    void qc.invalidateQueries({
      predicate: (q) => q.queryKey[0] === 'rules' && q.queryKey[2] !== 'publish-preview',
    });

  const toggleGolden = async (g: GoldenResult, on: boolean) => {
    setGoldenBusy(g.goldenId);
    try {
      const res = on
        ? await api.confirmGolden(g.goldenId, etag.current)
        : await api.unconfirmGolden(g.goldenId, etag.current);
      etag.current = res.etag;
      setConfirmed((c) => ({ ...c, [g.goldenId]: on }));
    } catch (e) {
      if (e instanceof ApiError && e.code === 'DRAFT_CHANGED') refreshRules();
      showError(e, { screen: 'Rules', action: on ? 'confirm golden' : 'unconfirm golden' });
    } finally {
      setGoldenBusy(null);
    }
  };

  const ackRisk = async (r: RiskText) => {
    setRiskBusy(r.code);
    try {
      const res = await api.ackRisk(r.code, { etag: etag.current, fingerprint: r.fingerprint });
      etag.current = res.etag;
      setAcked((a) => ({ ...a, [r.code]: true }));
    } catch (e) {
      // Риск изменился или черновик изменился — перечитать: текст и отпечаток риска будут новыми.
      if (e instanceof ApiError && (e.code === 'RISK_CHANGED' || e.code === 'DRAFT_CHANGED')) refreshRules();
      showError(e, { screen: 'Rules', action: 'ack risk' });
    } finally {
      setRiskBusy(null);
    }
  };

  const publish = useMutation({
    mutationFn: () => {
      if (!p) throw new Error('влияние не посчитано');
      return api.publish({
        etag: etag.current,
        previewId: p.previewId,
        validFrom,
        basis: {
          kind: kind as BasisKind,
          decided: decided.trim(),
          ...(isDoc ? { doc: doc.trim() } : {}),
          ...(question ? { questionCode: question } : {}),
        },
        seen: true,
        ...(plan ? { planId: plan.planId } : {}),
      });
    },
    onSuccess: (res) => {
      showInfo(res.jobId === null ? say.publishedFuture(res.version.validFrom) : T.publishedRecalc);
      void qc.invalidateQueries({ queryKey: rulesKeys.all });
      onPublished(res);
    },
    onError: (e) => {
      // Данные изменились после расчёта влияния (FR-039): посчитать заново и ждать повторного нажатия. По плану — ещё
      // план: его влияние «Сейчас» тоже новое.
      if (e instanceof ApiError && e.code === 'PREVIEW_STALE') {
        setStale(true);
        setSeed((s) => ({ ...s, etag: etag.current, n: s.n + 1 }));
        if (plan) void qc.invalidateQueries({ queryKey: rulesKeys.plan(role, plan.planId) });
        return;
      }
      // План устарел (FR-038): публиковать по нему нельзя — окно закрывается, страница перечитывает план и черновик.
      if (plan && e instanceof ApiError && e.code === 'PLAN_STALE') {
        refreshRules();
        showError(e, { screen: 'Rules', action: 'publish plan' });
        onStale();
        return;
      }
      if (
        e instanceof ApiError &&
        [
          'DRAFT_CHANGED',
          'RISK_NOT_ACKNOWLEDGED',
          'RISK_CHANGED',
          'GOLDEN_FAILED',
          'NOTHING_TO_PUBLISH',
        ].includes(e.code)
      )
        refreshRules();
      showError(e, {
        screen: 'Rules',
        action: 'publish',
        retry: isTransient(e) ? () => publish.mutate() : undefined,
      });
    },
  });

  // Почему «Опубликовать» недоступно — первая причина по порядку (FR-037).
  const reason: string | null =
    draft.changes.length === 0
      ? T.changesEmpty
      : errors.length > 0
        ? T.whyErrors
        : !dateOk
          ? say.whyDate(draft.earliestValidFrom)
          : pending && !failed
            ? T.whyComputing
            : failed || !p
              ? T.whyImpact
              : calcErrors > 0
                ? say.whyCalcErrors(calcErrors)
                : goldens.some((g) => !isConfirmed(g))
                  ? T.whyGoldens
                  : risks.some((r) => !isAcked(r))
                    ? T.whyRisks
                    : kind === ''
                      ? T.whyKind
                      : decided.trim() === ''
                        ? T.whyDecided
                        : isDoc && doc.trim() === ''
                          ? T.whyDoc
                          : !seen
                            ? T.whySeen
                            : null;
  const busy = publish.isPending || goldenBusy !== null || riskBusy !== null;

  const errorId = preview.error instanceof ApiError ? preview.error.requestId : null;
  const split = dateOk && !pending ? splitOf(p) : [];
  const future = validFrom > view.today && (p?.summary?.changedDays ?? 0) === 0;

  let impact: ReactNode;
  if (pending && !preview.isError) {
    impact = (
      <div aria-busy="true" aria-label={LABEL.computing} className="flex items-center gap-3">
        <span className="text-ink-2">{LABEL.computing}</span>
        <Skeleton className="h-3 min-w-24 flex-1" />
      </div>
    );
  } else if (failed || !p?.summary) {
    const why = errorText(preview.error, '');
    impact = (
      <div role="alert" className="flex flex-wrap items-center gap-3">
        <p className="min-w-0 flex-1 text-bad">
          {why ? `${T.previewError} ${why}.` : T.previewError}
          {errorId && <span className="block text-[12px] text-ink-3">{say.errorCode(errorId)}</span>}
        </p>
        <Button onClick={() => void preview.refetch()}>{LABEL.retry}</Button>
      </div>
    );
  } else {
    impact = (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="min-w-0 flex-1 basis-[260px] space-y-0.5">
          {future ? <p>{T.futureNoDays}</p> : <p>{impactLine(say.impactFrom(validFrom), p.summary)}</p>}
        </div>
        {(p.days ?? []).length > 0 && (
          <Button variant="ghost" onClick={() => setDays(true)}>
            {LABEL.whichDays}
          </Button>
        )}
        <DaysDialog open={days} days={p.days ?? []} onClose={() => setDays(false)} />
      </div>
    );
  }

  // Вопрос реестра из плана, которого нет в списке реестра, всё равно виден в выборе.
  const registryList = registry.data ?? [];
  const extraQuestion = question && !registryList.some((q) => q.code === question) ? question : null;

  return (
    <div className="space-y-5 text-[13.5px]">
      {plan && (
        <Callout tone="accent" icon={Bot}>
          {sayPlan.prepared(plan.preparedBy.client, ruDateTime(plan.preparedBy.frozenAt, tzOffset))}
        </Callout>
      )}
      <Section label={LABEL.whatChanges}>
        <ul className="list-disc space-y-0.5 pl-5 text-ink-2">
          {draft.changes.map((c) => (
            <li key={`${c.group}.${c.field}`}>{c.text}</li>
          ))}
        </ul>
      </Section>

      <div className="space-y-2">
        {plan ? (
          <Section label={LABEL.validFrom}>
            <p className="tabular-nums">{ruDate(plan.validFrom)}</p>
            <p className="text-[12px] text-ink-3">{PLAN_T.dateFixed}</p>
          </Section>
        ) : (
          <>
            <label htmlFor={`${ids}-from`} className="block text-[13.5px] font-medium text-ink">
              {LABEL.validFrom}
            </label>
            <input
              id={`${ids}-from`}
              type="date"
              data-autofocus
              min={draft.earliestValidFrom}
              value={dateInput}
              aria-invalid={!dateOk || undefined}
              onChange={(e) => onDate(e.target.value)}
              className={`${inputCls} max-w-[200px] tabular-nums`}
            />
          </>
        )}
        {futureBoundary && !plan && (
          <Callout tone="accent" icon={Info}>
            {say.futureBoundary(draft.earliestValidFrom, base ? localDate(base.publishedAt, tzOffset) : null)}
          </Callout>
        )}
        {split.map((t) => (
          <Callout key={t.id} tone="warn" icon={TriangleAlert}>
            {say.twoVersions(t.name, validFrom)}
          </Callout>
        ))}
        <div className="flex items-start gap-2.5 rounded-[10px] border border-dashed border-line-strong px-3 py-2.5">
          <ChartColumn size={16} strokeWidth={1.75} className="mt-0.5 shrink-0 text-ink-3" aria-hidden />
          <div className="min-w-0 flex-1">{impact}</div>
        </div>
        {stale && (
          <Callout tone="warn" icon={TriangleAlert}>
            {T.previewStale}
          </Callout>
        )}
      </div>

      {goldens.length > 0 && (
        <Section label={LABEL.goldensChanged}>
          <ul className="space-y-2">
            {goldens.map((g) => (
              <li key={g.goldenId} className="space-y-1.5 rounded-[8px] border border-line px-3 py-2">
                <p>{goldenChangeText(g.title ?? g.code, g.before, g.after)}</p>
                <label className={CHOICE}>
                  <input
                    type="checkbox"
                    checked={isConfirmed(g)}
                    disabled={goldenBusy === g.goldenId}
                    onChange={(e) => void toggleGolden(g, e.target.checked)}
                    className={CHECK}
                  />
                  <span>{T.goldenOk}</span>
                </label>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {errors.length > 0 && (
        <div className="space-y-1.5">
          <ChecksList errors={errors} risks={[]} byId={byId} />
        </div>
      )}

      {risks.length > 0 && (
        <Section label={LABEL.warnings}>
          <ul className="space-y-2">
            {risks.map((r) => (
              <li key={r.code} className="space-y-1.5 rounded-[8px] bg-warn-soft px-3 py-2 text-warn">
                <p className="flex items-start gap-2">
                  <TriangleAlert size={16} strokeWidth={1.75} className="mt-px shrink-0" aria-hidden />
                  <span>{r.text}</span>
                </p>
                {r.example && <p className="text-ink-2">{`${LABEL.riskExample} ${r.example}`}</p>}
                <label className={`${CHOICE} text-ink`}>
                  <input
                    type="checkbox"
                    checked={isAcked(r)}
                    disabled={isAcked(r) || riskBusy === r.code}
                    onChange={(e) => {
                      if (e.target.checked) void ackRisk(r);
                    }}
                    className={CHECK}
                  />
                  <span>{LABEL.ackRisk}</span>
                </label>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <div className="space-y-1.5">
        <p id={`${ids}-kind`} className="text-[13.5px] font-medium text-ink">
          {LABEL.basisKind}
        </p>
        {plan && <p className="text-[12px] text-ink-3">{PLAN_T.dialogBasis}</p>}
        <div
          role="radiogroup"
          aria-labelledby={`${ids}-kind`}
          className="flex flex-wrap gap-x-4 gap-y-1 max-sm:flex-col"
        >
          {PUBLISH_BASIS_KINDS.map((k) => (
            <label key={k} className={`${CHOICE} items-center`}>
              <input
                type="radio"
                name={`${ids}-kind`}
                checked={kind === k}
                onChange={() => setKind(k)}
                className="h-4 w-4 shrink-0 accent-accent"
              />
              <span>{BASIS_KIND_TEXT[k]}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <label htmlFor={`${ids}-decided`} className="block text-[13.5px] font-medium text-ink">
          {LABEL.decided}
        </label>
        <textarea
          id={`${ids}-decided`}
          rows={3}
          maxLength={1000}
          value={decided}
          placeholder={T.decidedPlaceholder}
          aria-describedby={`${ids}-decided-hint`}
          onChange={(e) => setDecided(e.target.value)}
          className={textareaCls}
        />
        <p id={`${ids}-decided-hint`} className="text-[12px] text-ink-3">
          {T.decidedHint}
        </p>
      </div>

      {isDoc && (
        <div className="space-y-1.5">
          <label htmlFor={`${ids}-doc`} className="block text-[13.5px] font-medium text-ink">
            {LABEL.doc}
          </label>
          <input
            id={`${ids}-doc`}
            type="text"
            maxLength={100}
            value={doc}
            placeholder={T.docPlaceholder}
            onChange={(e) => setDoc(e.target.value)}
            className={`${inputCls} max-w-[320px]`}
          />
        </div>
      )}

      <div className="space-y-1.5">
        <label htmlFor={`${ids}-question`} className="block text-[13.5px] font-medium text-ink">
          {LABEL.question}
        </label>
        <select
          id={`${ids}-question`}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          aria-describedby={`${ids}-question-hint`}
          className={`${inputCls} max-w-full`}
        >
          <option value="">{LABEL.questionNone}</option>
          {extraQuestion && <option value={extraQuestion}>{extraQuestion}</option>}
          {registryList.map((q) => (
            <option key={q.code} value={q.code}>
              {`${q.code} — ${q.title}`}
            </option>
          ))}
        </select>
        <p id={`${ids}-question-hint`} className="text-[12px] text-ink-3">
          {T.questionHint}
        </p>
      </div>

      <label className={CHOICE}>
        <input type="checkbox" checked={seen} onChange={(e) => setSeen(e.target.checked)} className={CHECK} />
        <span>{T.seen}</span>
      </label>
      <p className="text-[12.5px] text-ink-2">{T.selfApproved}</p>

      <div
        data-footer
        className="sticky bottom-0 -mx-6 -mb-6 flex flex-wrap items-center justify-end gap-2 border-t border-line bg-surface px-6 py-3 max-sm:-mx-4 max-sm:-mb-5 max-sm:px-4 max-sm:pb-[max(12px,env(safe-area-inset-bottom))] max-sm:[&>button]:flex-auto"
      >
        {reason && (
          <p id={`${ids}-reason`} className="mr-auto text-[12.5px] text-ink-2 max-sm:w-full">
            {reason}
          </p>
        )}
        <Button onClick={onCancel}>{LABEL.cancel}</Button>
        <Button
          variant="primary"
          icon={Send}
          disabled={reason !== null || busy}
          aria-describedby={reason ? `${ids}-reason` : undefined}
          onClick={() => {
            setStale(false);
            publish.mutate();
          }}
        >
          {LABEL.publish}
        </Button>
      </div>
    </div>
  );
}

/**
 * Окно «Опубликовать изменения». Содержимое живёт, пока окно открыто: при новом открытии введённое начинается с
 * начала. Закрыть при введённом — сначала вопрос «Закрыть окно публикации?».
 */
export function PublishDialog({
  open,
  view,
  draft,
  plan,
  tzOffset = '+05:00',
  onClose,
  onPublished,
  onStale,
}: {
  open: boolean;
  view: RulesView;
  draft: DraftView;
  /** Публикация по плану ИИ-агента (005): дата и основание — из плана, запрос — с `planId`. */
  plan?: PlanResponse | undefined;
  tzOffset?: string;
  onClose: () => void;
  onPublished: (res: PublishResponse) => void;
  /** `409 PLAN_STALE`: по плану публиковать нельзя — окно закрывается без вопроса. */
  onStale?: (() => void) | undefined;
}) {
  const dirty = useRef(false);
  const [asking, setAsking] = useState(false);
  useEffect(() => {
    if (!open) {
      dirty.current = false;
      setAsking(false);
    }
  }, [open]);
  const requestClose = () => {
    if (dirty.current) setAsking(true);
    else onClose();
  };
  const onDirty = useRef((d: boolean) => {
    dirty.current = d;
  }).current;
  return (
    <>
      <Modal
        open={open}
        onClose={requestClose}
        title={LABEL.publishTitle}
        description={T.publishIntro}
        width="w-[720px]"
      >
        <PublishBody
          view={view}
          draft={draft}
          plan={plan}
          tzOffset={tzOffset}
          onCancel={requestClose}
          onDirty={onDirty}
          onStale={() => {
            dirty.current = false;
            (onStale ?? onClose)();
          }}
          onPublished={(res) => {
            dirty.current = false;
            onPublished(res);
          }}
        />
      </Modal>
      <AlertDialog
        open={asking}
        onClose={() => setAsking(false)}
        title={LABEL.closeAsk}
        icon={TriangleAlert}
        tone="warn"
        description={T.closeAskText}
      >
        <Button data-autofocus onClick={() => setAsking(false)}>
          {LABEL.backToDialog}
        </Button>
        <Button
          variant="danger"
          onClick={() => {
            setAsking(false);
            dirty.current = false;
            onClose();
          }}
        >
          {LABEL.closeDialog}
        </Button>
      </AlertDialog>
    </>
  );
}
